// =============================================================================
// wastageRows.js — PURE, dependency-free shaper for kitchen wastage rows.
//
// Kept separate from wastageSync.js (which pulls in the Supabase client) so this
// core logic can be unit-tested in plain Node with zero setup — no browser, no
// network, no env. Given the resolved site uuid and the kitchen tile's per-entry
// objects, it returns the exact rows to insert into wastage_events (migration
// 0060). No PIN and no staff name ever go in: staff is stamped server-side from
// the login token by the DB trigger.
// =============================================================================

// idem_key: stable per entry so a retried/double-tapped submit collapses to ONE
// row (wastage_events has a UNIQUE index on idem_key), while two genuinely
// different taps of the same ingredient in a day stay two rows. The kitchen tile
// already stamps each entry with a unique client id (Date.now()+random) — the
// perfect seed. Mirrors stockSync.idem's join+cap; inlined here so this stays a
// pure unit with no imports.
export function wastageIdemKey(siteKey, entryId) {
  return ["wastage", siteKey, entryId].filter((p) => p != null && p !== "").join("|").slice(0, 200);
}

// entries: the SAME array the kitchen tile builds for the Sheet write, each
// { id, ingredient, quantity, reason, staffName, time, dateStr }.
// day: the London trading date 'YYYY-MM-DD'. occurredAtIso: optional override
// (a backfill passes the row's real historical instant; a live tap omits it so
// the DB default now() stamps the submit moment).
export function wastageRowsFrom({ siteId, siteKey, entries, day, occurredAtIso } = {}) {
  if (!siteId || !Array.isArray(entries)) return [];
  return entries
    .map((e) => {
      if (!e) return null;
      const qty = Number(e.quantity);
      const item = e.ingredient ? String(e.ingredient) : "";
      // Never write an empty or non-positive waste row — the DB also refuses
      // qty <= 0, but filtering here avoids a swallowed rejection for the batch.
      if (!item || !Number.isFinite(qty) || qty <= 0) return null;
      const row = {
        site_id: siteId,
        item_name: item,
        qty,
        // Store exactly what the chef picked (free text, faithful to the tile),
        // defaulting to 'Not specified' the same way the tile does.
        reason: e.reason ? String(e.reason) : "Not specified",
        event_date: day,
        idem_key: wastageIdemKey(siteKey, e.id),
      };
      if (occurredAtIso) row.occurred_at = occurredAtIso;
      return row;
    })
    .filter(Boolean);
}
